import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  listTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  duplicateTemplate,
  listPipelineScenarios,
  createScenarioFromTemplate,
  deletePipelineScenario,
  activateScenario,
  deactivateScenario,
  listPipelineChannelTargets,
  addPipelineChannelTarget,
  updateChannelTarget,
  deleteChannelTarget,
} from '../../../api/scenarios';
import { getPipelines, updatePipeline } from '../../../api/client';
import ScenarioBuilder from '../../../components/ScenarioBuilder';
import { useAuth } from '../../../contexts/AuthContext';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// Default workflow data structure for new templates
const getDefaultWorkflowData = () => ({
  nodes: [
    {
      id: 'visual-perception',
      type: 'metricGroup',
      position: { x: 100, y: 100 },
      data: { groupName: 'Visual Perception', status: 100, level: 0 },
    },
    {
      id: 'encoder',
      type: 'metricGroup',
      position: { x: 300, y: 100 },
      data: { groupName: 'Encoder', status: 100, level: 1 },
    },
    {
      id: 'packager',
      type: 'metricGroup',
      position: { x: 500, y: 100 },
      data: { groupName: 'Packager', status: 100, level: 2 },
    },
    {
      id: 'cdn-analytics',
      type: 'metricGroup',
      position: { x: 700, y: 100 },
      data: { groupName: 'CDN Analytics', status: 100, level: 3 },
    },
  ],
  edges: [
    { id: 'e1', source: 'visual-perception', target: 'encoder' },
    { id: 'e2', source: 'encoder', target: 'packager' },
    { id: 'e3', source: 'packager', target: 'cdn-analytics' },
  ],
});

// Generate metric_config from workflow_data and errors.
// Every node field the push service reads must be copied across here — the
// runtime config is what gets published, workflow_data only drives the canvas.
const generateMetricConfig = (workflowData, errorsData = null) => {
  const groups = {};
  workflowData.nodes.forEach((node) => {
    groups[node.data.groupName] = {
      enabled: true,
      status: node.data.status,
      level: node.data.level,
      paths: node.data.paths || [],
      inline: node.data.inline !== false,
      update_threshold: node.data.update_threshold || 10,
      ...(node.data.metrics?.length ? { metrics: node.data.metrics } : {}),
    };
  });

  const config = { groups };

  // Include errors if provided
  if (errorsData) {
    config.errors = errorsData;
  }

  return config;
};

function E2EScenarios() {
  const { canEditTemplates } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState('');
  const [pipelineScenarios, setPipelineScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Check if user can edit templates (SuperAdmin only)
  const canEdit = canEditTemplates();

  // Dialog states
  const [templateDialog, setTemplateDialog] = useState({ open: false, mode: 'create', template: null });
  const [scenarioDialog, setScenarioDialog] = useState({ open: false });
  const [builderDialog, setBuilderDialog] = useState({ open: false, template: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', item: null });

  // Form states
  const [templateForm, setTemplateForm] = useState({ name: '', description: '' });
  const [scenarioForm, setScenarioForm] = useState({ templateId: '', name: '', description: '' });

  // Per-pipeline E2E integration settings: a list of channels (each with its own
  // channel key, base path, and path variants) + a shared push schedule.
  const [e2eSettings, setE2ESettings] = useState({
    channels: [
      { id: null, channelKey: '', basePath: '', pathVariants: [{ name: 'HLS', suffix: 'HLS.CLOUDFRONT' }] },
    ],
    pushEnabled: false,
    pushInterval: 60,
  });
  // Target ids loaded from the backend, used to reconcile deletions on save.
  const [originalTargetIds, setOriginalTargetIds] = useState([]);

  // Path variant names the builder can offer. A group publishes to a variant by
  // name, so the picker must show the names this pipeline's channels actually
  // define — a name that is not here resolves to nothing at push time.
  const availablePathNames = useMemo(() => {
    const names = new Set(['Channel']);
    (e2eSettings.channels || []).forEach((ch) => {
      (ch.pathVariants || []).forEach((v) => {
        if (v?.name) names.add(v.name);
      });
    });
    return Array.from(names);
  }, [e2eSettings.channels]);
  const [savingSettings, setSavingSettings] = useState(false);

  const showMessage = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadTemplates = useCallback(async () => {
    try {
      const response = await listTemplates();
      setTemplates(response.templates || []);
    } catch (error) {
      showMessage('Failed to load templates', 'error');
    }
  }, []);

  const loadPipelines = useCallback(async () => {
    try {
      const response = await getPipelines();
      // API returns array directly, not { pipelines: [...] }
      setPipelines(Array.isArray(response) ? response : (response.pipelines || []));
    } catch (error) {
      showMessage('Failed to load pipelines', 'error');
    }
  }, []);

  const loadPipelineScenarios = useCallback(async () => {
    if (!selectedPipeline) {
      setPipelineScenarios([]);
      return;
    }
    try {
      const response = await listPipelineScenarios(selectedPipeline);
      setPipelineScenarios(response.scenarios || []);
    } catch (error) {
      showMessage('Failed to load pipeline scenarios', 'error');
    }
  }, [selectedPipeline]);

  useEffect(() => {
    loadTemplates();
    loadPipelines();
  }, [loadTemplates, loadPipelines]);

  useEffect(() => {
    loadPipelineScenarios();
  }, [loadPipelineScenarios]);

  const loadE2ESettings = useCallback(async () => {
    if (!selectedPipeline) return;
    const pipeline = pipelines.find((p) => p.id === selectedPipeline);
    const pushEnabled = pipeline?.e2e_push_enabled || false;
    const pushInterval = pipeline?.e2e_push_interval || 60;
    let channels = [];
    let targetIds = [];
    try {
      const resp = await listPipelineChannelTargets(selectedPipeline);
      const targets = resp.targets || [];
      channels = targets.map((t) => ({
        id: t.id,
        channelKey: t.channel_key || '',
        basePath: t.base_path || '',
        pathVariants: (t.path_variants && t.path_variants.length)
          ? t.path_variants.map((v) => ({ name: v.name, suffix: v.suffix }))
          : [{ name: 'HLS', suffix: 'HLS.CLOUDFRONT' }],
      }));
      targetIds = targets.map((t) => t.id);
    } catch (error) {
      // No channel targets yet — fall back to a single blank channel below
    }
    if (channels.length === 0) {
      // No targets persisted yet — seed one blank channel, carrying over the
      // legacy pipeline-level channel key if one was set before targets existed.
      const legacyKey = pipeline?.e2e_channel_key || '';
      channels = [{
        id: null,
        channelKey: legacyKey,
        basePath: legacyKey ? `${legacyKey}.ENTERTAINMENT` : '',
        pathVariants: [{ name: 'HLS', suffix: 'HLS.CLOUDFRONT' }],
      }];
    }
    setOriginalTargetIds(targetIds);
    setE2ESettings({ channels, pushEnabled, pushInterval });
  }, [selectedPipeline, pipelines]);

  useEffect(() => {
    loadE2ESettings();
  }, [loadE2ESettings]);

  // Template handlers
  const handleCreateTemplate = () => {
    setTemplateForm({ name: '', description: '' });
    setTemplateDialog({ open: true, mode: 'create', template: null });
  };

  const handleEditTemplate = (template) => {
    setTemplateForm({ name: template.name, description: template.description || '' });
    setTemplateDialog({ open: true, mode: 'edit', template });
  };

  const handleOpenBuilder = (template) => {
    setBuilderDialog({ open: true, template });
  };

  const handleSaveTemplate = async () => {
    setLoading(true);
    try {
      const workflowData = templateDialog.template?.workflow_data || getDefaultWorkflowData();
      const metricConfig = generateMetricConfig(workflowData);

      if (templateDialog.mode === 'create') {
        await createTemplate({
          name: templateForm.name,
          description: templateForm.description,
          workflow_data: workflowData,
          metric_config: metricConfig,
          is_default: false,
        });
        showMessage('Template created successfully');
      } else {
        await updateTemplate(templateDialog.template.id, {
          name: templateForm.name,
          description: templateForm.description,
        });
        showMessage('Template updated successfully');
      }
      setTemplateDialog({ open: false, mode: 'create', template: null });
      loadTemplates();
    } catch (error) {
      showMessage(error.response?.data?.detail || 'Failed to save template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      await duplicateTemplate(template.id);
      showMessage('Template duplicated successfully');
      loadTemplates();
    } catch (error) {
      showMessage('Failed to duplicate template', 'error');
    }
  };

  const handleDeleteTemplate = async () => {
    try {
      await deleteTemplate(deleteDialog.item.id);
      showMessage('Template deleted successfully');
      setDeleteDialog({ open: false, type: '', item: null });
      loadTemplates();
    } catch (error) {
      showMessage(error.response?.data?.detail || 'Failed to delete template', 'error');
    }
  };

  const handleBuilderSave = async (workflowData, errorsData = null) => {
    setLoading(true);
    try {
      const metricConfig = generateMetricConfig(workflowData, errorsData);
      await updateTemplate(builderDialog.template.id, {
        workflow_data: workflowData,
        metric_config: metricConfig,
      });
      showMessage('Template workflow saved successfully');
      setBuilderDialog({ open: false, template: null });
      loadTemplates();
    } catch (error) {
      showMessage('Failed to save workflow', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Pipeline Scenario handlers
  const handleCreateScenario = () => {
    setScenarioForm({ templateId: '', name: '', description: '' });
    setScenarioDialog({ open: true });
  };

  const handleSaveScenario = async () => {
    if (!selectedPipeline || !scenarioForm.templateId) {
      showMessage('Please select a pipeline and template', 'error');
      return;
    }
    setLoading(true);
    try {
      await createScenarioFromTemplate(selectedPipeline, {
        template_id: scenarioForm.templateId,
        name: scenarioForm.name || undefined,
        description: scenarioForm.description || undefined,
      });
      showMessage('Scenario created successfully');
      setScenarioDialog({ open: false });
      loadPipelineScenarios();
    } catch (error) {
      showMessage(error.response?.data?.detail || 'Failed to create scenario', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateScenario = async (scenario) => {
    try {
      await activateScenario(selectedPipeline, scenario.id);
      showMessage(`Scenario "${scenario.name}" activated`);
      loadPipelineScenarios();
    } catch (error) {
      showMessage('Failed to activate scenario', 'error');
    }
  };

  const handleDeactivateScenario = async (scenario) => {
    try {
      await deactivateScenario(selectedPipeline, scenario.id);
      showMessage(`Scenario "${scenario.name}" deactivated`);
      loadPipelineScenarios();
    } catch (error) {
      showMessage('Failed to deactivate scenario', 'error');
    }
  };

  const handleDeleteScenario = async () => {
    try {
      await deletePipelineScenario(selectedPipeline, deleteDialog.item.id);
      showMessage('Scenario deleted successfully');
      setDeleteDialog({ open: false, type: '', item: null });
      loadPipelineScenarios();
    } catch (error) {
      showMessage('Failed to delete scenario', 'error');
    }
  };

  const handleSaveE2ESettings = async () => {
    if (!selectedPipeline) return;
    setSavingSettings(true);
    try {
      // Upsert every channel that has both a channel key and a base path.
      const upserts = e2eSettings.channels
        .filter((ch) => ch.channelKey && ch.basePath)
        .map((ch) => {
          const targetData = {
            channel_key: ch.channelKey,
            base_path: ch.basePath,
            path_variants: (ch.pathVariants || [])
              .filter((v) => v.name && v.suffix)
              .map((v) => ({ name: v.name, suffix: v.suffix })),
            is_enabled: true,
          };
          return ch.id
            ? updateChannelTarget(ch.id, targetData)
            : addPipelineChannelTarget(selectedPipeline, targetData);
        });
      // Any previously loaded target that isn't a fully-filled-in channel anymore
      // gets removed — this covers both removed rows and existing rows that were
      // cleared in place (id kept but key/path emptied out).
      const keepIds = e2eSettings.channels
        .filter((ch) => ch.id && ch.channelKey && ch.basePath)
        .map((ch) => ch.id);
      const deletions = originalTargetIds
        .filter((id) => !keepIds.includes(id))
        .map((id) => deleteChannelTarget(id));

      await Promise.all([...upserts, ...deletions]);

      // Persist push schedule on the pipeline; first channel's key is informational only
      // (the push loop uses the full channel-target list, not this field).
      const firstChannel = e2eSettings.channels[0];
      await updatePipeline(selectedPipeline, {
        e2e_channel_key: firstChannel?.channelKey || null,
        e2e_push_enabled: e2eSettings.pushEnabled,
        e2e_push_interval: e2eSettings.pushInterval,
      });
      showMessage('E2E integration settings saved');
      await loadPipelines();
      await loadE2ESettings();
    } catch (error) {
      showMessage(error.response?.data?.detail || 'Failed to save E2E settings', 'error');
      // The fan-out Promise.all may have applied some ops before one rejected —
      // reload so the panel reflects the backend's true state.
      await loadE2ESettings();
    } finally {
      setSavingSettings(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 100) return 'success';
    if (status === 50) return 'warning';
    return 'error';
  };

  const getStatusLabel = (status) => {
    if (status === 100) return 'Good';
    if (status === 50) return 'Warning';
    return 'Critical';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          E2E Scenario Builder
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Scenario Templates" />
          <Tab label="Pipeline Scenarios" />
        </Tabs>
      </Paper>

      {/* Templates Tab */}
      <TabPanel value={activeTab} index={0}>
        {!canEdit && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You have read-only access to scenario templates. Contact a Super Admin to make changes.
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateTemplate}
            >
              New Template
            </Button>
          )}
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Groups</TableCell>
                <TableCell>Default</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>
                    <Typography fontWeight="medium">{template.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {template.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {template.workflow_data?.nodes?.map((node) => (
                        <Chip
                          key={node.id}
                          label={node.data.groupName}
                          size="small"
                          color={getStatusColor(node.data.status)}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {template.is_default && (
                      <Chip label="Default" size="small" color="primary" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {canEdit ? (
                      <>
                        <Tooltip title="Edit Workflow">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenBuilder(template)}
                            color="primary"
                          >
                            <AccountTreeIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Details">
                          <IconButton
                            size="small"
                            onClick={() => handleEditTemplate(template)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate">
                          <IconButton
                            size="small"
                            onClick={() => handleDuplicateTemplate(template)}
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setDeleteDialog({ open: true, type: 'template', item: template })}
                              disabled={template.is_default}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip title="View Workflow">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenBuilder(template)}
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {templates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">No templates found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Pipeline Scenarios Tab */}
      <TabPanel value={activeTab} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel>Select Pipeline</InputLabel>
            <Select
              value={selectedPipeline}
              label="Select Pipeline"
              onChange={(e) => setSelectedPipeline(e.target.value)}
            >
              {pipelines.map((pipeline) => (
                <MenuItem key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateScenario}
            disabled={!selectedPipeline}
          >
            Add Scenario from Template
          </Button>
        </Box>

        {selectedPipeline && canEdit && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              E2E Integration Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Channel keys, paths, and push schedule used when publishing this pipeline's
              active scenario to Touchstream. Scenario structure is defined in the templates above.
            </Typography>

            {e2eSettings.channels.map((ch, ci) => (
              <Box
                key={ch.id ?? `new-${ci}`}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Channel {ci + 1}</Typography>
                  <Button
                    size="small"
                    color="error"
                    disabled={e2eSettings.channels.length <= 1}
                    onClick={() => setE2ESettings((s) => ({
                      ...s,
                      channels: s.channels.filter((_, cj) => cj !== ci),
                    }))}
                  >
                    Remove channel
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label="Channel Key"
                    size="small"
                    value={ch.channelKey}
                    onChange={(e) => setE2ESettings((s) => {
                      const channels = [...s.channels];
                      channels[ci] = { ...channels[ci], channelKey: e.target.value };
                      return { ...s, channels };
                    })}
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    label="Base Path"
                    size="small"
                    value={ch.basePath}
                    onChange={(e) => setE2ESettings((s) => {
                      const channels = [...s.channels];
                      channels[ci] = { ...channels[ci], basePath: e.target.value };
                      return { ...s, channels };
                    })}
                    placeholder={ch.channelKey ? `${ch.channelKey}.ENTERTAINMENT` : 'channelKey.ENTERTAINMENT'}
                    sx={{ minWidth: 260 }}
                  />
                </Box>
                <Box sx={{ width: '100%', mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Path Variants</Typography>
                  {ch.pathVariants.map((v, vi) => (
                    <Box key={vi} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField label="Name" size="small" value={v.name}
                        onChange={(e) => setE2ESettings((s) => {
                          const channels = [...s.channels];
                          const pv = [...channels[ci].pathVariants];
                          pv[vi] = { ...pv[vi], name: e.target.value };
                          channels[ci] = { ...channels[ci], pathVariants: pv };
                          return { ...s, channels };
                        })} sx={{ width: 140 }} />
                      <TextField label="Suffix" size="small" value={v.suffix}
                        placeholder="HLS.CLOUDFRONT"
                        onChange={(e) => setE2ESettings((s) => {
                          const channels = [...s.channels];
                          const pv = [...channels[ci].pathVariants];
                          pv[vi] = { ...pv[vi], suffix: e.target.value };
                          channels[ci] = { ...channels[ci], pathVariants: pv };
                          return { ...s, channels };
                        })} sx={{ width: 220 }} />
                      <Button size="small" color="error" onClick={() => setE2ESettings((s) => {
                        const channels = [...s.channels];
                        channels[ci] = {
                          ...channels[ci],
                          pathVariants: channels[ci].pathVariants.filter((_, vj) => vj !== vi),
                        };
                        return { ...s, channels };
                      })}>Remove</Button>
                    </Box>
                  ))}
                  <Button size="small" onClick={() => setE2ESettings((s) => {
                    const channels = [...s.channels];
                    channels[ci] = {
                      ...channels[ci],
                      pathVariants: [...channels[ci].pathVariants, { name: '', suffix: '' }],
                    };
                    return { ...s, channels };
                  })}>+ Add variant</Button>
                </Box>
              </Box>
            ))}

            <Button
              size="small"
              variant="outlined"
              sx={{ mb: 2 }}
              onClick={() => setE2ESettings((s) => ({
                ...s,
                channels: [
                  ...s.channels,
                  { id: null, channelKey: '', basePath: '', pathVariants: [{ name: 'HLS', suffix: 'HLS.CLOUDFRONT' }] },
                ],
              }))}
            >
              + Add channel
            </Button>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={e2eSettings.pushEnabled}
                    onChange={(e) => setE2ESettings((s) => ({ ...s, pushEnabled: e.target.checked }))}
                  />
                }
                label="Enable Auto Push"
              />
              <TextField
                label="Push Interval (s)"
                type="number"
                size="small"
                value={e2eSettings.pushInterval}
                onChange={(e) =>
                  setE2ESettings((s) => ({
                    ...s,
                    pushInterval: Math.max(10, Math.min(300, parseInt(e.target.value, 10) || 60)),
                  }))
                }
                inputProps={{ min: 10, max: 300 }}
                sx={{ width: 160 }}
                disabled={!e2eSettings.pushEnabled}
              />
              <Button variant="outlined" onClick={handleSaveE2ESettings} disabled={savingSettings}>
                {savingSettings ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Paper>
        )}

        {selectedPipeline ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Based On</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pipelineScenarios.map((scenario) => (
                  <TableRow key={scenario.id}>
                    <TableCell>
                      <Typography fontWeight="medium">{scenario.name}</Typography>
                      {scenario.description && (
                        <Typography variant="body2" color="text.secondary">
                          {scenario.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {scenario.template_name || 'Custom'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={scenario.is_active ? 'Active' : 'Inactive'}
                        color={scenario.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{scenario.priority}</TableCell>
                    <TableCell align="right">
                      {scenario.is_active ? (
                        <Tooltip title="Deactivate">
                          <IconButton
                            size="small"
                            onClick={() => handleDeactivateScenario(scenario)}
                            color="warning"
                          >
                            <StopIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Activate">
                          <IconButton
                            size="small"
                            onClick={() => handleActivateScenario(scenario)}
                            color="success"
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteDialog({ open: true, type: 'scenario', item: scenario })}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {pipelineScenarios.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">
                        No scenarios for this pipeline
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">Select a pipeline to view and manage its scenarios</Alert>
        )}
      </TabPanel>

      {/* Template Create/Edit Dialog */}
      <Dialog
        open={templateDialog.open}
        onClose={() => setTemplateDialog({ open: false, mode: 'create', template: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {templateDialog.mode === 'create' ? 'Create Template' : 'Edit Template'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            fullWidth
            value={templateForm.name}
            onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
            sx={{ mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={templateForm.description}
            onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
          />
          {templateDialog.mode === 'create' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              After creating the template, use the workflow editor to configure metric groups and their status.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialog({ open: false, mode: 'create', template: null })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={loading || !templateForm.name}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scenario from Template Dialog */}
      <Dialog
        open={scenarioDialog.open}
        onClose={() => setScenarioDialog({ open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Scenario from Template</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Select Template</InputLabel>
            <Select
              value={scenarioForm.templateId}
              label="Select Template"
              onChange={(e) => setScenarioForm({ ...scenarioForm, templateId: e.target.value })}
            >
              {templates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Scenario Name (optional, defaults to template name)"
            fullWidth
            value={scenarioForm.name}
            onChange={(e) => setScenarioForm({ ...scenarioForm, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={2}
            value={scenarioForm.description}
            onChange={(e) => setScenarioForm({ ...scenarioForm, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScenarioDialog({ open: false })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveScenario}
            disabled={loading || !scenarioForm.templateId}
          >
            {loading ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, type: '', item: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{deleteDialog.item?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, type: '', item: null })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={deleteDialog.type === 'template' ? handleDeleteTemplate : handleDeleteScenario}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scenario Builder Dialog */}
      {builderDialog.open && (
        <ScenarioBuilder
          open={builderDialog.open}
          template={builderDialog.template}
          pathOptions={availablePathNames}
          pathOptionsKnown={Boolean(selectedPipeline)}
          onClose={() => setBuilderDialog({ open: false, template: null })}
          onSave={handleBuilderSave}
          readOnly={!canEdit}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default E2EScenarios;
